import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';
import { OverlayContext } from 'ui/utils/OverlayManager';
import { D3TooltipSkill } from 'tooltips';
import EditingContext from 'store/editingContext';
import { PassiveIcon, SkillHeader, SkillActive, SkillInfo, SkillParams, SkillBoxes } from './skillInfo';
import { getStats } from 'logic/stats';
import { getPassiveBonus } from 'logic/skills';
import scrollIntoView from 'scroll-into-view-if-needed';
import { useDragDrop, usePassiveMover } from 'components/dragDrop';

const _L = Locale("ui-skills.js");

const passiveLevels = [10, 20, 30, 70];

function PassivePicker({ onResolve, index }) {
  const charClass = Store.useProfile(profile => profile.class);
  const passives = Store.useProfile(profile => profile.passives);
  const passive = passives[index];

  const total = Object.keys(Data.passives[charClass]).length;

  return (
    <div className="d3p-PassivePicker" style={{height: 59 * Math.ceil(total / 3) + 12}}>
      {Object.entries(Data.passives[charClass]).map(([id, info]) => {
        const disabled = passives.includes(id);
        const style = {
          backgroundPosition: `${-info.index}em ${disabled ? -1 : 0}em`,
          top: 15 + 59 * Math.floor(info.index / 3),
        };
        if (info.index >= Math.floor(total / 3) * 3) {
          if (total % 3 === 1) {
            style.left = 74;
          } else {
            style.left = 45 + 59 * (info.index % 3);
          }
        } else {
          style.left = 15 + 59 * (info.index % 3);
        }
        const onClick = disabled ? null : () => onResolve(id);
        return (
          <D3TooltipSkill key={id} charClass={charClass} skill={id} touchAction="none">
            <span className={classNames(`d3p-passive-icon d3p-class-${charClass}`, {"d3p-selected": id === passive})} style={style} onClick={onClick}>
              <span className="d3p-passive-frame"/>
            </span>
          </D3TooltipSkill>
        );
      })}
    </div>
  );
}

const PassiveActiveBox = React.memo(function PassiveActiveBox({ charClass, passive, info }) {
  const state = Store.useState();
  const stats = getStats(state);

  if (!getPassiveBonus(charClass, passive, stats)) {
    return null;
  }
  return <SkillActive info={info}/>;
});

export const PassiveCard = React.memo(function PassiveCard({ index }) {
  const [editing, setEditing] = React.useState(false);
  const charClass = Store.useProfile(profile => profile.class);
  const dispatch = Store.useDispatch();
  const overlays = React.useContext(OverlayContext);

  const extraRef = React.useRef();
  const picker = React.useCallback(e => {
    if (extraRef.current.contains(e.target)) {
      return;
    }
    setEditing(true);
    overlays.popup(e, <PassivePicker index={index}/>).then(id => id && dispatch.profile.setPassive(index, id)).finally(() => setEditing(false));
  }, [overlays, dispatch, index]);

  const passive = Store.useProfile(profile => profile.passives[index]);
  let info = passive && Data.passives[charClass][passive];

  const onClear = React.useCallback(e => {
    if (extraRef.current.contains(e.target)) {
      return;
    }
    dispatch.profile.setPassive(index, null);
    e.preventDefault();
  }, [dispatch, index]);

  const headerRef = React.useRef();
  const onEdit = React.useCallback(({ type, index: targetIndex }) => {
    if (type === "passive" && index === targetIndex) {
      scrollIntoView(headerRef.current, {scrollMode: "if-needed", block: "center", inline: "center"});
      const rect = headerRef.current.getBoundingClientRect();
      picker({clientX: (rect.left + rect.right) / 2, clientY: (rect.top + rect.bottom) / 2});
      return true;
    }
  }, [index, picker]);
  EditingContext.useHandler(onEdit);

  const passiveMover = usePassiveMover();

  const [dragClass, drag, drop] = useDragDrop("PASSIVE", {
    canDrag: () => info != null,
    options: {dropEffect: "move"},
    begin: () => ({type: "PASSIVE", charClass, id: passive}),
    end: (item, monitor) => passiveMover({index}, monitor.getDropResult()),
  }, {
    drop: () => ({index}),
  });

  const empty = (!info || dragClass === "d3p-dragging");

  return (
    <div className={classNames("d3p-SkillCard d3p-passive", dragClass, {"d3p-editing": editing})} ref={drop}>
      <D3TooltipSkill charClass={charClass} skill={info && passive} disabled={empty} touchAction="none">
        <SkillHeader icon={<PassiveIcon charClass={charClass} info={empty ? null : info} alt={passiveLevels[index]} ref={drag}/>}
                     empty={!info}
                     name={info ? info.name : _L`Choose Skill`}
                     onClick={picker}
                     onContextMenu={info && onClear}
                     ref={headerRef}
        >
          <div className="d3p-passive-extra" ref={extraRef}>
            {!!(info && info.setActive) && <PassiveActiveBox charClass={charClass} passive={passive} info={info}/>}
            {!!(info && info.params) && <SkillParams info={info}/>}
            {!!(info && info.boxnames) && <SkillBoxes info={info}/>}
            {!!(info && info.info) && <SkillInfo type="passive" id={passive}/>}
          </div>
        </SkillHeader>
      </D3TooltipSkill>
    </div>
  );
});

export const ExtraPassiveCard = React.memo(function PassiveCard({ passive }) {
  const charClass = Store.useProfile(profile => profile.class);
  const info = passive && Data.passives[charClass][passive];
  if (!info) return null;
  return (
    <div className="d3p-SkillCard d3p-passive">
      <D3TooltipSkill charClass={charClass} skill={info && passive}>
        <SkillHeader icon={<PassiveIcon charClass={charClass} info={info}/>}
                     empty={!info}
                     name={info.name}
        >
          <div className="d3p-passive-extra">
            {!!(info && info.setActive) && <PassiveActiveBox charClass={charClass} passive={passive} info={info}/>}
            {!!(info && info.params) && <SkillParams info={info}/>}
            {!!(info && info.info) && <SkillInfo type="passive" id={passive}/>}
          </div>
        </SkillHeader>
      </D3TooltipSkill>
    </div>
  );
});

export default function PassivesSection() {
  const passives = Store.useProfile(profile => profile.passives);
  const stats = Store.useSelector(getStats);
  const extras = stats.extra_passive?.filter(passive => !passives.includes(passive));
  return (
    <React.Fragment>
      <h3 className="d3p-skill-category">{_L`Passives`}</h3>
      {[0, 1, 2, 3].map(index => <PassiveCard key={index} index={index}/>)}
      {!!extras?.length && <h3 className="d3p-skill-category">{_L`Granted Passives`}</h3>}
      {!!extras?.length && extras.map(passive => <ExtraPassiveCard key={passive} passive={passive}/>)}
    </React.Fragment>
  );
}
